<script context="module" lang="ts">
  import type { Author, Reaction } from "models/forums";
  import type { JSONContent } from "@tiptap/core";
  import type { ThreadPost, ThreadPostPermissions } from "@outverse-video/outverse-api-client-lib";

  export interface ReplyProps {
    uuid: string;
    seqNum: number;
    author: Author | null;
    reactions?: Reaction[];
    content: JSONContent;
    created?: string;
    modified?: string;
    replyToPostAuthor?: Author;
    replyToPostUuid?: string | null;
    repliesCount?: number;
    isRemoved?: boolean;
    myPermissions?: ThreadPostPermissions;
    upvoteCount: number;
    userUpvoted: boolean;
  }
</script>

<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import Reactions from "$components/forum//Reactions/Reactions.svelte";
  import ForumCard from "$components/forum/cards/ForumCard.svelte";
  import Footer from "$components/forum/cards/Footer.svelte";
  import OVIcon from "$components/+shared/OVIcon/OVIcon.svelte";
  import Avatar from "$components/+shared/Avatar/Avatar.svelte";
  import Button from "$components/+design-system/Button/Button.svelte";
  import ToolTip from "$components/+shared/ToolTip/ToolTip.svelte";
  import DialogueModal from "$components/+shared/Modal/DialogueModal.svelte";
  import Upvote from "$components/forum/Upvotes/Upvote.svelte";
  import type { DropdownAction } from "$components/+design-system/Dropdown/fields/Action.svelte";
  import { communitySlug } from "$stores/community";
  import { alerts } from "$stores/alert";
  import { isCommunityMember } from "$stores/permissions";
  import { thread, activeThread, activeThreadLocked, threadAuthor } from "$stores/thread";
  import { hasPermission } from "$stores/permissions";
  import { forum } from "app/stores/forum";
  import { saveToClipboard } from "$utils/clipboard";
  import { replyPanel } from "$stores/replyPanel";
  import { preventInnerEventPropagation } from "$utils/events";
  import cx from "classnames";
  import api from "$utils/api";
  import {
    ForumThreadPermission,
    ThreadPostPermission,
  } from "@outverse-video/outverse-api-client-lib";
  import Reader from "app/components/editor/Reader.svelte";
  import { threadURL } from "app/utils/urls";
  import { replyComposer } from "app/stores/replyComposer";
  import type { BaseEmoji } from "$components/+design-system/EmojiPicker/types";
  import {
    Cross,
    MarkAsSolution,
    Flag,
    Link,
    Pencil,
    ReplyingTo,
    Trash,
    Up,
    Down,
    CheckFilled,
  } from "$components/+shared/OVIcon";

  export let threadUuid: string;
  export let threadSlug: string;
  export let forumSpaceUuid: string;
  export let forumSpaceSlug: string;
  export let post: ReplyProps;
  export let canReply: boolean = false;
  export let isQuote: boolean = false;
  export let showReplies: boolean = false;
  export let focused: boolean = false;
  export let isReply: boolean = false;
  export let isSolving: boolean = false;
  export let isReplyUpvotesEnabled: boolean;

  const dispatch = createEventDispatcher();

  let menuOptions: DropdownAction[] = [];
  let canUpvote: boolean = hasPermission(post.myPermissions, ThreadPostPermission.UpvoteThreadpost);
  let isDeleteDialogueOpen = false;

  const addEmojiReaction = (emojiObject: BaseEmoji) => {
    thread.addPostReaction($communitySlug, post.uuid, emojiObject.native);
  };
  const toggleReaction = ({ emoji }: { emoji: string }) => {
    thread.togglePostReaction($communitySlug, post.uuid, emoji);
  };

  let selectedSolution: string | null | undefined;
  $: canUpdateSolution = hasPermission(
    $activeThread?.myPermissions,
    ForumThreadPermission.ChangeForumthread
  );

  const updateSolution = async () => {
    try {
      if (selectedSolution) {
        await api.communitiesThreadsSolve({
          communitySlug: $communitySlug,
          uuid: threadUuid,
          switchSolution: {
            uuid: selectedSolution,
          },
        });
      } else {
        await api.communitiesThreadsUnsolve({
          communitySlug: $communitySlug,
          uuid: threadUuid,
        });
      }
      thread.fetch(threadUuid, $communitySlug);
      selectedSolution = undefined;
      return;
    } catch (Error) {
      alerts.error("There was a problem updating the thread solution.");
    }

    selectedSolution = undefined;
  };

  $: {
    menuOptions = [];
    if (
      post.myPermissions &&
      hasPermission(post.myPermissions, ThreadPostPermission.ChangeThreadpost)
    ) {
      menuOptions.push({
        onClick: () => {
          replyComposer.editPost(threadUuid, post.uuid, post.content);
        },
        title: "Edit post",
        icon: Pencil,
      });
    }

    if (
      !isQuote &&
      post.myPermissions &&
      hasPermission(post.myPermissions, ThreadPostPermission.DeleteThreadpost)
    ) {
      menuOptions.push({
        onClick: () => (isDeleteDialogueOpen = true),
        title: "Delete post",
        icon: Trash,
      });
    }

    menuOptions.push({
      onClick: () =>
        // removing query post string if it already exists
        saveToClipboard(
          threadURL(
            {
              spaceUuid: forumSpaceUuid,
              threadUuid: threadUuid,
              communitySlug: $communitySlug,
              spaceSlug: forumSpaceSlug,
              threadSlug: threadSlug,
            },
            true
          ) + `?post=${post.seqNum}`,
          alerts.add
        ),
      title: "Copy link",
      icon: Link,
    });
    menuOptions.push({
      onClick: async () => {
        try {
          await api.communitiesPostsReport({
            communitySlug: $communitySlug,
            uuid: post.uuid,
          });
        } catch (Error) {
          // regardless of whether response is successful, behave as if it was
        }
        alerts.success("Post has been reported.");
      },
      title: "Report post",
      icon: Flag,
    });
  }

  function openReplyPanel(event: Event) {
    if (preventInnerEventPropagation(event)) {
      return;
    }

    if (post.isRemoved) {
      return;
    }

    replyPanel.clearFetched();
    // TODO: we are currently casting post of type ReplyProps to ThreadPost, probably the post itself should be ThreadPost
    // as the api defines it like that and not ReplyProps which is a custom frontend type,
    // this causes a weird divergence between the api and the frontend
    // so it should be fixed in the future
    replyPanel.setActiveReply(post as unknown as ThreadPost);
    if (!$activeThread?.forumThread) {
      return;
    }

    replyPanel.setActiveThread({
      ...$activeThread.forumThread,
      myPermissions: $activeThread.myPermissions,
    });
    replyPanel.clearAndShow(post.uuid, threadUuid, $communitySlug);
  }

  const deleteReply = async () => {
    thread.deletePost({
      uuid: post.uuid,
      addAlert: alerts.add,
    });
  };
</script>

<!-- the stopPropagation is needed to permit a quoted post within a post to be opened when clicking on the link -->
<div
  role="button"
  tabindex="0"
  aria-label="open reply panel"
  class="cursor-pointer"
  on:click|stopPropagation={(event) => openReplyPanel(event)}
  on:keypress|stopPropagation={(event) => openReplyPanel(event)}
>
  <ForumCard
    type="reply"
    author={post.author}
    time={post.created}
    modified={post.isRemoved ? undefined : post.modified}
    highlight={focused}
    fields={post.isRemoved ? [] : menuOptions}
    isAuthorReply={post.author?.uuid ? $threadAuthor === post.author?.uuid : false}
    {isSolving}
    {isQuote}
    {showReplies}
  >
    <!-- CARD BODY -->
    <svelte:fragment slot="solutionStatus">
      {#if !post.isRemoved && !isQuote}
        {#if !($forum.forumSpace.solvableThreads && canUpdateSolution)}
          <!-- Show nothing -->
        {:else if $thread.activeThread?.forumThread.solution?.uuid === post.uuid}
          <button on:click={() => (selectedSolution = null)}>
            <ToolTip>
              <span slot="tip">Remove as solution</span>
              <div
                class={cx(
                  "flex cursor-pointer items-center gap-1 rounded border-[0.5px] px-2 py-1 text-xs leading-none",
                  "border-emerald-400/25 bg-emerald-400/10 hover:border-emerald-400/50 hover:bg-emerald-400/15"
                )}
              >
                <OVIcon icon={CheckFilled} class="text-emerald-400" size="size-3" />
                <span class="text-emerald-900 dark:text-emerald-100">Solution</span>
                <OVIcon
                  icon={Cross}
                  class="inline-block text-emerald-900 dark:text-emerald-100"
                  size="size-3"
                />
              </div>
            </ToolTip>
          </button>
        {:else}
          <button on:click={() => (selectedSolution = post.uuid)}>
            <ToolTip>
              <span slot="tip">Mark as solution</span>
              <OVIcon
                icon={MarkAsSolution}
                class="inline-block text-gray-400 hover:text-gray-600 dark:hover:text-gray-100"
              />
            </ToolTip>
          </button>
        {/if}
      {/if}
    </svelte:fragment>

    {#if post.replyToPostUuid}
      <!-- 'replying to' button -->
      <button
        type="button"
        class={cx(
          "inline-flex items-center gap-1 rounded bg-gray-300/40 p-1 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-300/80 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-gray-100",
          { "cursor-default": isQuote }
        )}
        on:click={() => {
          if (post.replyToPostUuid) {
            thread.goToPostByUUID(post.replyToPostUuid);
          }
        }}
      >
        <OVIcon icon={ReplyingTo} class="inline-block" /> Replying to
        <!-- check for author, parent post may have been deleted -->
        {#if post.replyToPostAuthor}
          <Avatar
            mainImage={post.replyToPostAuthor?.profileImage}
            backgroundColor={post.replyToPostAuthor.profileColor}
            label={post.replyToPostAuthor.handle && post.replyToPostAuthor.handle[0]}
            wrapperClasses="ml-0.5 rounded-sm"
          />

          {post.replyToPostAuthor.handle}
        {:else}
          <span class="text-xs"> [deleted] </span>
        {/if}
      </button>
    {/if}

    <div class="mt-4 text-gray-700 dark:text-gray-300">
      {#if post.isRemoved}
        <span class="text-sm"> [deleted] </span>
      {:else}
        <Reader content={post.content} />
      {/if}
    </div>

    <Footer>
      <svelte:fragment slot="left-section">
        {#if !post.isRemoved && isReplyUpvotesEnabled}
          <Upvote
            variant="horizontal-small"
            disabled={!canUpvote}
            count={post.upvoteCount}
            isSelected={post.userUpvoted}
            addUpvote={() =>
              api.communitiesPostsUpvote({
                communitySlug: $communitySlug,
                uuid: post.uuid,
              })}
            removeUpvote={() =>
              api.communitiesPostsRemoveUpvote({
                communitySlug: $communitySlug,
                uuid: post.uuid,
              })}
          />
        {/if}
        {#if !post.isRemoved && !$activeThreadLocked && canReply}
          <Button
            variant="primary"
            label="Reply"
            size="small"
            on:click={() => {
              if (post.author) replyComposer.replyToPost(threadUuid, post.uuid, post.author);
            }}
          />
        {/if}
        {#if !post.isRemoved && !isReply && post.repliesCount !== undefined && post.repliesCount > 0}
          <!-- nested replies visibility toggle button -->
          <Button
            variant="primary"
            size="small"
            label={`${post.repliesCount} ${post.repliesCount > 1 ? "replies" : "reply"}`}
            on:click={() => dispatch("toggleReplies")}
            iconRight={!isQuote ? (showReplies ? Up : Down) : undefined}
            disabled={isQuote}
          />
        {/if}
      </svelte:fragment>
      <svelte:fragment slot="middle-section">
        {#if !isQuote && !post.isRemoved && post.reactions}
          <Reactions
            reactions={post.reactions}
            enabled={$isCommunityMember}
            on:reaction={(e) => toggleReaction(e.detail)}
            on:addReaction={(e) => addEmojiReaction(e.detail)}
          />
        {/if}
      </svelte:fragment>
    </Footer>
  </ForumCard>
</div>

{#if selectedSolution !== undefined}
  {#if $thread.activeThread?.forumThread.solution}
    <!-- There's an active solution -->
    {#if selectedSolution}
      <!-- User updated to a new solution -->
      <DialogueModal
        open
        title="Update solution"
        message="Are you sure you want to update the solution to this thread? This will remove the existing solution."
        confirmLabel="Update"
        on:confirm={updateSolution}
        on:cancel={() => (selectedSolution = undefined)}
      />
    {:else}
      <!-- User removed solution -->
      <DialogueModal
        open
        title="Remove solution"
        message="Are you sure you want to remove the solution to this thread?"
        confirmLabel="Remove"
        on:confirm={updateSolution}
        on:cancel={() => (selectedSolution = undefined)}
        danger
      />
    {/if}
  {:else}
    <!-- User selected a new solution -->
    <DialogueModal
      open
      title="Confirm solution"
      message="Are you sure you want to mark this reply as the solution to this thread?"
      confirmLabel="Confirm"
      on:confirm={updateSolution}
      on:cancel={() => (selectedSolution = undefined)}
      danger
    />
  {/if}
{/if}

{#if isDeleteDialogueOpen}
  <!-- User deleted a reply -->
  <DialogueModal
    open
    title="Delete reply"
    message="Are you sure you want to delete this post?"
    confirmLabel="Confirm"
    on:confirm={deleteReply}
    on:cancel={() => (isDeleteDialogueOpen = false)}
    danger
  />
{/if}
